import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { sendApiRequest } from '../utils';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import {
  FiDollarSign,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiShield,
  FiInfo,
  FiArrowRight,
  FiLock,
  FiCreditCard,
  FiCode,
  FiUser,
  FiTrendingDown,
  FiPercent
} from 'react-icons/fi';
import {
  FaWallet,
  FaHandHoldingUsd,
  FaChartLine,
  FaShieldAlt,
  FaPiggyBank
} from 'react-icons/fa';

const WithdrawalRequestForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.user_info[0]?.user);
  const [isLoading, setIsLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [availableAmount, setAvailableAmount] = useState(0);
  const [isReportClosed, setIsReportClosed] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [deductions, setDeductions] = useState({
    adminFee: 0,
    tds: 0,
    netAmount: 0
  });

  // Fetch available amount, check report status, and check for pending requests on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch available amount
        const statsResponse = await sendApiRequest('api/overview/stats', {}, 'GET');
        if (statsResponse.status === 200) {
          setAvailableAmount(statsResponse.data.data.walletStats.totalIncome);
        }

        // Check if current month's report is closed
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();

        const reportResponse = await sendApiRequest(
          `api/packages/admin/closing-report?year=${currentYear}&month=${currentMonth}`,
          {},
          'GET'
        );

        if (reportResponse.status === 200) {
          setIsReportClosed(reportResponse.data.data.isClosed);
        }

        // Check for pending withdrawal requests
        const pendingRequestResponse = await sendApiRequest(
          'api/withdrawal-requests/user',
          { status: 'pending' },
          'GET'
        );

        if (pendingRequestResponse.status === 200 && pendingRequestResponse.data.withdrawalRequests.length > 0) {
          setHasPendingRequest(true);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch required data');
      }
    };

    fetchData();
  }, []);

  // State for form fields and success message
  const [formData, setFormData] = useState({
    withdrawalType: 'BANK (Minimum allowed Rs. 1000)',
    withdrawalAmount: '',
    date: '',
    accountNumber: user?.bank_account_number || '',
    bankName: user?.bank_name || '',
    branchName: user?.branch_name || '',
    ifscCode: user?.ifsc_code || '',
    accountType: user?.account_type || 'Saving Account',
  });

  const [error, setError] = useState('');

  // Set current date on mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setFormData((prev) => ({ ...prev, date: today }));
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'withdrawalAmount') {
      validateAmount(value);
    }
  };

  // Validate withdrawal amount
  const validateAmount = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    if (numAmount < 1000 && numAmount > 0) {
      setError('Withdrawal amount must be at least ₹1,000.');
    } else if (numAmount > availableAmount) {
      setError('Withdrawal amount exceeds available balance.');
    } else {
      setError('');
      // Calculate deductions
      const adminFee = numAmount * 0.05;
      const tds = numAmount * 0.05;
      const netAmount = numAmount - adminFee - tds;
      
      setDeductions({
        adminFee,
        tds,
        netAmount
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isReportClosed) {
      toast.error('Cannot submit withdrawal request. Current month\'s report is not closed yet.');
      return;
    }

    if (hasPendingRequest) {
      toast.error('You already have a pending withdrawal request. Please wait for it to be processed.');
      return;
    }

    if (!formData.withdrawalAmount || parseFloat(formData.withdrawalAmount) < 1000) {
      setError('Please enter a valid withdrawal amount of at least ₹1,000.');
      return;
    }
    if (error) return;

    setIsLoading(true);
    try {
      const requestPayload = {
        amount: parseFloat(formData.withdrawalAmount),
        bankDetails: {
          accountNumber: formData.accountNumber,
          bankName: formData.bankName,
          branchName: formData.branchName,
          ifscCode: formData.ifscCode,
          accountType: formData.accountType
        }
      };

      const response = await sendApiRequest('api/withdrawal-requests', requestPayload, 'POST');

      if (response.data?.success) {
        toast.success('Withdrawal request submitted successfully!');
        setFormData((prev) => ({ ...prev, withdrawalAmount: '' }));
        navigate('/dashboard');
        setError('');
        // Refresh available amount
        const statsResponse = await sendApiRequest('api/overview/stats', {}, 'GET');
        if (statsResponse.status === 200) {
          setAvailableAmount(statsResponse.data.data.walletStats.totalIncome);
        }
      } else {
        const errorMessage = response.data?.message || 'Failed to submit withdrawal request';
        toast.error(errorMessage);
        setError(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while submitting the request';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const canSubmit = isReportClosed && !hasPendingRequest && !isLoading && !error && formData.withdrawalAmount >= 1000;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg mb-4">
            <FaHandHoldingUsd className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Withdrawal Request
          </h1>
          <p className="text-gray-500 mt-2 text-sm">Request a withdrawal from your wallet balance</p>
        </div>

        {/* Status Alerts */}
        <AnimatePresence>
          {!isReportClosed && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200"
            >
              <div className="flex items-start gap-3">
                <FiClock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Withdrawal Request Not Available</p>
                  <p className="text-sm text-amber-700 mt-1">Withdrawal requests can only be submitted after the current month's report is closed.</p>
                </div>
              </div>
            </motion.div>
          )}

          {hasPendingRequest && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200"
            >
              <div className="flex items-start gap-3">
                <FiAlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-800">Pending Request Exists</p>
                  <p className="text-sm text-yellow-700 mt-1">You already have a pending withdrawal request. Please wait for it to be processed before making a new request.</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <FiDollarSign className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-lg">Withdrawal Details</h2>
                <p className="text-indigo-100 text-sm">Fill the form to request withdrawal</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Available Balance Card */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center">
                    <FaWallet className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Available Balance</p>
                    <p className="text-2xl font-bold text-indigo-600">{formatAmount(availableAmount)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Minimum withdrawal</p>
                  <p className="text-sm font-semibold text-gray-700">₹1,000</p>
                </div>
              </div>
            </div>

            {/* Withdrawal Type */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <FiCreditCard className="w-4 h-4 text-indigo-500" />
                Withdrawal Type
              </label>
              <select
                name="withdrawalType"
                value={formData.withdrawalType}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed"
                disabled
              >
                <option>BANK (Minimum allowed ₹1,000)</option>
              </select>
            </div>

            {/* Withdrawal Amount */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <FiDollarSign className="w-4 h-4 text-indigo-500" />
                Withdrawal Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">₹</span>
                <input
                  type="number"
                  name="withdrawalAmount"
                  value={formData.withdrawalAmount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                  className={`w-full pl-8 pr-4 py-3 rounded-xl border transition-all outline-none ${
                    error ? 'border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200'
                  } bg-white`}
                  required
                  disabled={!isReportClosed || hasPendingRequest}
                />
              </div>
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-1 text-sm text-red-500 flex items-center gap-1"
                >
                  <FiAlertCircle className="w-3 h-3" />
                  {error}
                </motion.p>
              )}
            </div>

            {/* Date */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <FiClock className="w-4 h-4 text-indigo-500" />
                Request Date
              </label>
              <input
                type="text"
                name="date"
                value={formData.date}
                readOnly
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Bank Details Section */}
            <div className="pt-2">
              <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FaPiggyBank className="w-4 h-4 text-indigo-500" />
                Bank Account Details
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Account Number</label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <FiCreditCard className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{formData.accountNumber || 'Not provided'}</span>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Bank Name</label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <FiCreditCard className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{formData.bankName || 'Not provided'}</span>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Branch Name</label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <FiInfo className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{formData.branchName || 'Not provided'}</span>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">IFSC Code</label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <FiCode className="w-4 h-4 text-gray-400" />
                    <span className="font-mono text-sm text-gray-700">{formData.ifscCode || 'Not provided'}</span>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Account Type</label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <FiUser className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{formData.accountType || 'Saving Account'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Deductions Section */}
            {formData.withdrawalAmount >= 1000 && !error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="border-t border-gray-200 pt-4"
              >
                <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FiTrendingDown className="w-4 h-4 text-amber-500" />
                  Fee & Deductions
                </h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2">
                    <div className="flex items-center gap-2">
                      <FiPercent className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">Admin Fee (5%)</span>
                    </div>
                    <span className="font-medium text-gray-800">{formatAmount(deductions.adminFee)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2">
                    <div className="flex items-center gap-2">
                      <FiPercent className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">TDS (5%)</span>
                    </div>
                    <span className="font-medium text-gray-800">{formatAmount(deductions.tds)}</span>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <span className="font-semibold text-gray-800">Net Amount</span>
                    <span className="text-xl font-bold text-emerald-600">{formatAmount(deductions.netAmount)}</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Note */}
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-start gap-2">
                <FiInfo className="w-4 h-4 text-blue-600 mt-0.5" />
                <p className="text-xs text-blue-700">
                  To update account details, please go to the <strong>Update Profile</strong> page.
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <motion.button
                type="submit"
                disabled={!canSubmit}
                whileHover={canSubmit ? { scale: 1.02 } : {}}
                whileTap={canSubmit ? { scale: 0.98 } : {}}
                className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                  canSubmit
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : !isReportClosed ? (
                  <>
                    <FiClock className="w-4 h-4" />
                    Report Not Closed
                  </>
                ) : hasPendingRequest ? (
                  <>
                    <FiAlertCircle className="w-4 h-4" />
                    Pending Request Exists
                  </>
                ) : (
                  <>
                    <FaHandHoldingUsd className="w-4 h-4" />
                    Submit Withdrawal Request
                    <FiArrowRight className="w-4 h-4" />
                  </>
                )}
              </motion.button>
            </div>

            {/* Security Note */}
            <div className="text-center pt-2">
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                <FiShield className="w-3 h-3" />
                Your withdrawal request is secure and encrypted
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default WithdrawalRequestForm;